import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex } from 'components';
import { P } from 'components/Typography/Typography';
import {
  Button,
  Radio,
  Select,
  SwitchButton,
  TextField
} from 'components/_form';
import type { IOption as INestedSelectOption } from 'components/_form/NestedSelect/NestedSelect';
import { NestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { t } from 'i18next';
import { BaseSyntheticEvent } from 'react';
import { AccordionItemButton } from 'react-accessible-accordion';
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister
} from 'react-hook-form';
import {
  StyledAccordionItemHeading,
  StyledAccordionItemPanel
} from '../../ProfileView.styled';

interface IOption {
  label: string;
  value: string;
}

interface Props {
  id?: string;
  register: UseFormRegister<FieldValues>;
  onSubmit: (
    e?: BaseSyntheticEvent<object, any, any> | undefined
  ) => Promise<void>;
  control: Control<FieldValues, any>;
  companyOptions?: IOption[];
  functionOptions?: IOption[];
  /** Drzewo `IOption[]` z `parseParentToChildrenAreasStructureToOptions` (pole `options`, nie `children`). */
  areaOptions?: INestedSelectOption[];
  /** Wirtualne „aktywny” z API (niezablokowany przez Devise). */
  accountActive?: boolean;
  /** Przy anulowaniu potwierdzenia lub błędzie API zwiększ, by zresetować przełącznik. */
  accountActiveSwitchKey?: number;
  onAccountActiveChange?: (nextActive: boolean) => void | Promise<void>;
}

export const BasicInformationForm = ({
  id,
  register,
  onSubmit,
  control,
  companyOptions = [],
  functionOptions = [],
  areaOptions = [],
  accountActive = true,
  accountActiveSwitchKey = 0,
  onAccountActiveChange
}: Props) => {
  return (
    <form onSubmit={onSubmit}>
      <StyledAccordionItemHeading>
        <AccordionItemButton>
          <Flex p={2}>
            <FontAwesomeIcon icon={faAddressCard} />
            <P ml={2} variant="body">
              {t('profile.basicInformation')}
            </P>
          </Flex>
        </AccordionItemButton>
      </StyledAccordionItemHeading>
      <StyledAccordionItemPanel>
        <Flex flexWrap="wrap">
          <Flex mr={2} width={['100%', '32%']}>
            {id && onAccountActiveChange && (
              <Flex my={1} justifyContent="center">
                <SwitchButton
                  key={`${accountActiveSwitchKey}-${accountActive}`}
                  label="administration.active?"
                  value={accountActive}
                  onChange={onAccountActiveChange}
                />
              </Flex>
            )}
            <TextField label="profile.idCode" {...register('id_code')} />
          </Flex>
          <Box mr={2} mt={1} width={['100%', '32%']}>
            <Controller
              control={control}
              name="company_id"
              render={({ field: { onChange, value } }) => (
                <Select
                  label="profile.userCompany"
                  options={companyOptions}
                  selectedOptions={value != null ? companyOptions.find((o) => o.value === String(value)) : undefined}
                  onChange={(opt) => onChange((opt as IOption)?.value ?? '')}
                />
              )}
            />
          </Box>
        </Flex>
        <Flex flexWrap="wrap">
          <Box mr={2} width={['100%', '32%']}>
            <TextField label="profile.name" {...register('first_name')} />
          </Box>
          <Box mr={2} width={['100%', '32%']}>
            <TextField
              label="profile.secondname"
              {...register('second_name')}
            />
          </Box>
          <Box mr={2} width={['100%', '32%']}>
            <TextField label="profile.surname" {...register('last_name')} />
          </Box>
        </Flex>
        <Flex flexWrap="wrap">
          <Box mr={2} width={['100%', '32%']}>
            <TextField label="common.language" {...register('language')} />
          </Box>
          <Box mr={2} width={['100%', '32%']}>
            <TextField label="profile.phone" {...register('phone')} />
          </Box>
          <Box mr={2} width={['100%', '32%']}>
            <TextField
              label="profile.landlinePhone"
              {...register('landline_phone')}
            />
          </Box>
        </Flex>
        <Flex flexWrap="wrap">
          <Box mr={[0, 2]} width={['100%', '49%']}>
            <Controller
              control={control}
              name="area_ids"
              render={({ field: { onChange, value } }) => {
                const roots = areaOptions ?? [];
                const selectedAreas = Array.isArray(value)
                  ? value
                      .map((vid) =>
                        findOptionByValueInTree(
                          roots,
                          typeof vid === 'string' ? vid : String(vid)
                        )
                      )
                      .filter((o): o is INestedSelectOption => !!o)
                  : [];
                return (
                  <NestedSelect
                    label={t('profile.mainArea')}
                    options={roots}
                    selectedOptions={selectedAreas}
                    setSelectedOptions={(opts) =>
                      onChange(
                        collapseSelectedAreaPermissionIds(roots, opts).map(
                          (id) => String(id)
                        )
                      )
                    }
                  />
                );
              }}
            />
          </Box>
          <Box width={['100%', '49%']}>
            <Controller
              control={control}
              name="function_ids"
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti
                  label={id ? 'profile.employeeRoles' : 'profile.myRoleInCompany'}
                  options={functionOptions}
                  selectedOptions={functionOptions.filter((o) =>
                    Array.isArray(value) ? value.includes(o.value) : false
                  )}
                  onChange={(opt) =>
                    onChange(Array.isArray(opt) ? opt.map((o) => o.value) : [])
                  }
                />
              )}
            />
          </Box>
        </Flex>

        {id && (
          <Flex flexDirection="column">
            <P variant="body" mb={2}>
              {t('employeesAndUsersView.tableHeaders.accountType')}
            </P>

            <Flex flexWrap="wrap" gap="8px">
              <Radio
                {...register('role')}
                label="administration.admin"
                value="admin"
              />
              <Radio
                {...register('role')}
                label="administration.user"
                value="user"
              />
              <Radio
                {...register('role')}
                label="administration.guest"
                value="guest"
              />
              <Radio
                {...register('role')}
                label="administration.consultant"
                value="consultant"
              />
            </Flex>
          </Flex>
        )}

        <Flex justifyContent="end">
          <Button variant="eucalyptus" type="submit">
            {t('common.save')}
          </Button>
        </Flex>
      </StyledAccordionItemPanel>
    </form>
  );
};
